import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { NewDocument } from './NewDocument';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Info from '@mui/icons-material/Info';
import { ButtonGroup, Grid, Tooltip } from '@mui/material';
import { UploadContainer } from './bulkUpload/UploadContainer';
import { ThumbBox } from './components/ThumbBox';
import generatePdfThumbnails from './thumb';
import { FileBox } from './components/FileBox';
import { ItemTypes } from './types';
import SavedDocuments from './components/SavedDocuments';
import PdfPageViewer from './components/PdfPageViewer';
import { useBulkFileUpload } from './bulkUpload/bulkFileHandling';
import { v4 as uuidv4 } from 'uuid';
import file from 'assets/img/file.png';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { ALL_UPLOAD_DOCUMENT_LIST_EXECUTED } from 'features/document/edit/documents/subscriptions';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { BUFFER_TEMP_FILES_QUERY, BUFFER_TEMP_FILES_VALIDITY } from 'features/document/edit/documents/queries';
import { DELETE_BUFFER_TEMP_FILES } from 'features/document/edit/documents/mutations';
import { useMutation } from '@apollo/client';
import { useToast } from 'utils/hooks';
import { convertBase64ToByteArray, convertImageToPdfBytes, uploadedFileIsImage } from 'utils/functions';
import { Button, Card, Dialog, Typography } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { BoxActionsWrapper, GridActionsWrapper } from 'components/common/CommonStyles';

const isPdf = type => type === 'application/pdf';
let lastPageId = 0;

const BulkDocumentsContainer = ({ documentId, siteId, documents, show, onToggle, saveDocument, loading, setLoading }) => {
  const addToast = useToast();
  const { t } = useTranslation();

  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('page');
  const [files, setFiles] = useState([]);
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [savedDocuments, setSavedDocuments] = useState({});
  const [selectedDocument, setselectedDocument] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(true);
  const [deleteBufferFilesDialog, setDeleteBufferFilesDialog] = useState(false);
  const [selectedPagesKeys, setSelectedPagesKeys] = useState([]);
  const [bulkSelectionStartId, setBulkSelectionStartId] = useState(null);
  const [pages, setPages] = useState([...Array(1).keys()].map(a => ({ index: a, item: null })));

  useGlobalSubscription(ALL_UPLOAD_DOCUMENT_LIST_EXECUTED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setLoading(false);
      setLocalDocuments(data.allUploadDocumentListExecuted);
    }
  });

  const updateFileThumbs = async file => {
    let isEncrypted = false;
    if (isPdf(file.type)) {
      const pdfText = await new Blob([convertBase64ToByteArray(file.data.split(',')[1])], {
        type: 'application/pdf'
      }).text();
      isEncrypted = pdfText.includes('Encrypt');
    }

    file.thumbs = isPdf(file.type) && !isEncrypted ? await generatePdfThumbnails(file.data, 100, file.fileId, file.name) : undefined;
    file.data = isPdf(file.type) ? file.data : undefined;

    if (file.thumbs?.length) {
      file.thumbs = file.thumbs.map(x => {
        return { ...x, correlationId: file.correlationId, id: ++lastPageId };
      });
    }

    setFiles(update => [
      ...update,
      {
        name: file.name,
        fileId: file.fileId,
        type: file.type,
        correlationId: file.correlationId,
        data: file.data,
        thumbs: file.thumbs
      }
    ]);
    if (file.isLast) setUploading(false);
  };

  const { data: bufferTempFilesValidity } = useQueryWithErrorHandling(BUFFER_TEMP_FILES_VALIDITY);

  const { loading: loadingBufferTempFiles, data: bufferData } = useQueryWithErrorHandling(BUFFER_TEMP_FILES_QUERY, {
    variables: { documentId, siteId },
    fetchPolicy: 'network-only',
    onError: _error => {
      setUploading(false);
    },
    onCompleted: async data => {
      if (!data) return;

      for (let bufferTempFile of data.bufferTempFiles) {
        const correlationId = uuidv4();
        const [fileId, ...rest] = bufferTempFile.name.split('_');
        await updateFileThumbs({
          correlationId,
          data: isPdf(bufferTempFile.contentType) ? 'data:application/pdf;base64,' + bufferTempFile.content : undefined,
          fileId: fileId,
          name: rest.join('_'),
          type: bufferTempFile.contentType
        });
      }

      if (data.bufferTempFiles.length > 0 && !data.bufferTempFiles.some(x => isPdf(x.contentType))) setMode('file');
    }
  });

  useEffect(() => {
    if (files.length !== bufferData?.bufferTempFiles?.length) return;
    setUploading(false);
  }, [files, setUploading, bufferData]);

  const [uploadDocument] = useBulkFileUpload({
    onError: _error => {
      setUploading(false);
    },
    onCompleted: async data => {
      const correlationId = data.uploadFile.correlationId;
      const uploadedFile = uploadedFiles.find(file => file.correlationId === correlationId);

      if (uploadedFile) {
        uploadedFile.fileId = data.uploadFile.fileId;
        uploadedFile.correlationId = correlationId;

        const updatedFiles = uploadedFiles.map(file => (file.correlationId === correlationId ? uploadedFile : file));
        setUploadedFiles(updatedFiles);
        await updateFileThumbs(uploadedFile);
      }
      if (!uploadedFiles.some(x => isPdf(x.type))) setMode('file');
    },
    setUploading,
    showNotifications: true
  });

  const onFileUploadDrop = async item => {
    if (item) {
      setUploading(true);
      const files = item.files ?? Array.from(item.target.files);

      for (const f of files) {
        const correlationId = uuidv4();

        let pdfBytes;
        if (uploadedFileIsImage(f.name)) {
          try {
            pdfBytes = await convertImageToPdfBytes(f);
          } catch (error) {
            setUploading(false);
            throw new Error(t(error.message));
          }
        }
        let uploadedFile;

        if (pdfBytes) {
          const pdfUint8Array = new Uint8Array(pdfBytes);
          const base64String = pdfUint8Array.reduce((binary, byte) => binary + String.fromCharCode(byte), '');

          uploadedFile = {
            data: 'data:application/pdf;base64,' + window.btoa(base64String),
            fileId: '',
            correlationId,
            type: 'application/pdf',
            name: `${f.name.replace(/\.[^/.]+$/, '')}.pdf`,
            isLast: files.indexOf(f) === files.length - 1
          };
        } else {
          const data = isPdf(f.type)
            ? await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = e => resolve(e.target.result);
                reader.onerror = e => reject(e);
                reader.readAsDataURL(f);
              })
            : undefined;

          uploadedFile = {
            data,
            fileId: '',
            correlationId,
            type: f.type,
            name: f.name,
            isLast: files.indexOf(f) === files.length - 1
          };
        }
        setUploadedFiles(prevUploadedFiles => [...prevUploadedFiles, uploadedFile]);

        await uploadDocument(documentId, siteId, correlationId, f, t);
      }
    }
  };

  const onSave = useCallback(() => {
    const data = [];
    Object.keys(savedDocuments).forEach(key => {
      let doc = savedDocuments[key];
      data.push({
        attachmentId: doc.attachment.attachmentId,
        attachmentName: doc.attachment.attachmentName,
        attachmentTypeId: doc.attachment.attachmentTypeId,
        contextId: doc.attachment.contextId,
        entityTitle: doc.attachment.entityTitle,
        pages: doc.pages.map(a => ({
          file: a.item.fileId,
          fileName: a.item.fileName,
          page: isNaN(+a.item.page) ? -1 : +a.item.page,
          rotationAngle: a.item.rotationAngle
        }))
      });
    });
    saveDocument(data);
  }, [saveDocument, savedDocuments]);

  const onPreview = useCallback(
    (item, canRotate) => {
      setPreview(null);
      if (item) {
        let file = files.find(x => x.correlationId === item?.correlationId)?.data;
        if (item.type === ItemTypes.PAGE) {
          setPreview({
            fileId: item.fileId,
            page: item.page - 1,
            item,
            file,
            canRotate
          });
        } else if (item.type === ItemTypes.FILE) {
          setPreview({
            fileId: item.fileId,
            page: null,
            item,
            file
          });
        } else {
          const savedDocument = savedDocuments[item].pages[0].item;
          file = files.find(x => x.correlationId === savedDocument.correlationId)?.data;
          if (savedDocument.type === ItemTypes.FILE) {
            setPreview({
              fileId: savedDocuments[item].pages[0].item.fileId,
              page: null,
              item: savedDocuments[item].pages[0].item,
              file
            });
          } else {
            setPreview({ ...savedDocuments[item], files, isDocument: true });
          }
        }
      }
      setOpen(true);
    },
    [setPreview, savedDocuments, files]
  );

  const onAddDocument = useCallback(
    data => {
      setSavedDocuments(prev => ({ ...prev, [data.attachment.attachmentName]: data }));
    },
    [setSavedDocuments]
  );

  const onEdit = useCallback(
    item => {
      setselectedDocument(savedDocuments[item]);
    },
    [setselectedDocument, savedDocuments]
  );

  const onRemove = useCallback(item => {
    setSavedDocuments(prev => {
      const copy = { ...prev };
      delete copy[item];
      return copy;
    });
  }, []);

  useEffect(() => {
    const updatedselectedPagesKeys = Object.values(savedDocuments).flatMap(document =>
      document.pages.map(page => page.item.storageKey || page.item.correlationId)
    );

    setSelectedPagesKeys(updatedselectedPagesKeys);
  }, [savedDocuments]);

  const getFileThumb = useCallback(
    _file => {
      return _file.isPdf ? files.find(a => a === _file)?.thumbs[0].thumbnail : file;
    },
    [files]
  );
  const onCancel = useCallback(() => {
    onToggle();
  }, [onToggle]);

  const [deleteBufferTempFiles] = useMutation(DELETE_BUFFER_TEMP_FILES, {
    onError: error => {
      addToast(error.message, 'error');
    },
    onCompleted: () => {
      setFiles([]);
    }
  });

  const onDeleteBufferTempFiles = useCallback(() => {
    deleteBufferTempFiles({
      variables: {
        documentId,
        siteId
      }
    });
  }, [deleteBufferTempFiles, documentId, siteId]);

  const onFileMode = useCallback(() => setMode('file'), [setMode]);
  const onPageMode = useCallback(() => setMode('page'), [setMode]);

  const handleItemSelection = useCallback(
    (item, bulkSelection) => {
      if (bulkSelection && !bulkSelectionStartId) {
        setBulkSelectionStartId(item.id);
      }
      let newSelectedFiles = [...selectedFiles];
      const allThumbs = files.flatMap(file => file.thumbs);
      const page = allThumbs.find(x => x.id === item.id);

      if (bulkSelection) {
        allThumbs.forEach(thumb => {
          if (selectedFiles.some(f => f.id === thumb.id)) return;
          if (
            (bulkSelectionStartId !== null && bulkSelectionStartId <= item.id && bulkSelectionStartId <= thumb.id && thumb.id <= item.id) ||
            (bulkSelectionStartId >= item.id && item.id <= thumb.id && thumb.id <= bulkSelectionStartId) ||
            item.id === thumb.id
          )
            newSelectedFiles.push({
              ...thumb,
              name: <img style={{ width: '100%', height: '100%' }} alt='page' src={thumb.thumbnail} />,
              index: newSelectedFiles.length,
              type: item.type
            });
        });
        if (bulkSelectionStartId !== null) setBulkSelectionStartId(null);
      } else if (selectedFiles.some(x => x.storageKey === page.storageKey)) {
        newSelectedFiles = selectedFiles.filter(x => x.storageKey !== page.storageKey);
      } else {
        newSelectedFiles.push({
          ...page,
          name: <img style={{ width: '100%', height: '100%' }} alt='page' src={page.thumbnail} />,
          index: newSelectedFiles.length,
          type: item.type
        });
      }

      setSelectedFiles(() => newSelectedFiles);
    },
    [selectedFiles, files, bulkSelectionStartId]
  );

  return (
    <Dialog
      id='dms-pdf-dialog-title'
      open={show}
      fullScreen
      maxWidth='lg'
      transitionDuration={0}
      onClose={onToggle}
      title={
        <Grid container alignItems='center' gap={1} px={2}>
          <Typography fontWeight='bold'>{t('BulkPdf.Title')}</Typography>
          <Tooltip
            title={
              <span>
                {t('BulkPdf.ImageExtenstionsAllowedInfo')}
                <br />
                {t('BulkPdf.BufferFilesValabilityInfo', {
                  data: bufferTempFilesValidity?.bufferTempFilesValidity
                })}
              </span>
            }
          >
            <Info color='primary' />
          </Tooltip>
        </Grid>
      }
      content={
        <>
          {loadingBufferTempFiles ? (
            <WrappedFakeText lines={5} />
          ) : (
            <>
              <DndProvider backend={HTML5Backend}>
                <Card sx={{ mb: 3 }}>
                  <Grid container flexDirection='column' alignItems='center' gap={2}>
                    <ButtonGroup aria-label='outlined primary button group'>
                      <Button variant={mode === 'file' ? 'contained' : 'outlined'} onClick={onFileMode}>
                        {t('BulkPdf.FileMode', {
                          count: files.length
                        })}
                      </Button>
                      <Button variant={mode === 'page' ? 'contained' : 'outlined'} onClick={onPageMode}>
                        {t('BulkPdf.PageMode', {
                          count: files.reduce((total, item) => (item.thumbs !== undefined ? total + item.thumbs.length : total), 0)
                        })}
                      </Button>
                    </ButtonGroup>
                    <Grid item container gap={3}>
                      <Grid item flexGrow={1} py={3} display='flex' justifyContent='center' alignItems='center'>
                        {uploading ? (
                          <Typography variant='subtitle2' color='textPrimary'>
                            {t('BulkPdf.Loading')}
                          </Typography>
                        ) : (
                          <UploadContainer onDrop={onFileUploadDrop} />
                        )}
                      </Grid>
                      <Grid item maxWidth='80%' display='flex' flexWrap='nowrap' overflow='auto'>
                        {mode === 'file' &&
                          files.map((file, index) => (
                            <FileBox
                              key={index}
                              file={{
                                name: <img style={{ width: 100 }} alt='page' src={getFileThumb(file)} />,
                                fileName: file.name,
                                fileId: file.fileId,
                                correlationId: file.correlationId,
                                type: ItemTypes.FILE,
                                addedToComposeDoc: selectedPagesKeys.some(correlationId => correlationId === file.correlationId)
                              }}
                            />
                          ))}
                        {mode === 'page' &&
                          files
                            ?.map(file =>
                              file?.thumbs?.map((thumb, index) => (
                                <ThumbBox
                                  key={thumb.id}
                                  onPreview={onPreview}
                                  onSelectionChange={handleItemSelection}
                                  setSelectedFiles={setSelectedFiles}
                                  item={{
                                    id: thumb.id,
                                    index: index,
                                    name: <img style={{ width: '100%', height: '100%' }} alt='page' src={thumb.thumbnail} />,
                                    page: thumb.page,
                                    addedToComposeDoc: selectedPagesKeys.some(
                                      storageKey =>
                                        storageKey === thumb.storageKey && !selectedFiles.some(y => y.storageKey === thumb.storageKey)
                                    ),
                                    selected: selectedFiles.some(x => x.storageKey === thumb.storageKey),
                                    fileName: file.name,
                                    fileId: file.fileId,
                                    correlationId: file.correlationId,
                                    storageKey: thumb.storageKey,
                                    type: ItemTypes.PAGE
                                  }}
                                />
                              ))
                            )
                            .flat()}
                      </Grid>
                    </Grid>
                    {files.length > 0 && (
                      <GridActionsWrapper item alignSelf='end'>
                        {selectedFiles.length > 0 && mode === 'page' && (
                          <Button onClick={() => setSelectedFiles([])} variant='outlined'>
                            {t('BulkPdf.DeselectAll')}
                          </Button>
                        )}
                        <Tooltip title={t('BulkPdf.DeleteButtonTooltip')}>
                          <span>
                            <Button onClick={() => setDeleteBufferFilesDialog(true)} disabled={Object.keys(savedDocuments).length > 0}>
                              {t('BulkPdf.DeleteBufferFiles')}
                            </Button>
                          </span>
                        </Tooltip>
                      </GridActionsWrapper>
                    )}
                  </Grid>
                  {/*<div style={{ margin: "0 20px 10px 0" }}>
              <Typography variant="body2">
                Size limit for each file is <span style={{ fontWeight: "bold" }}>10MB</span>. Accepted file types:{" "}
                <span style={{ fontWeight: "bold" }}>PDF, DOCX, XLS, JPEG, PNG</span>. After you upload bulk files you
                can drag and drop each of them to the corresponding document type
              </Typography>
                        </div>*/}
                </Card>

                <NewDocument
                  documents={localDocuments}
                  saveDocument={onAddDocument}
                  onPreview={onPreview}
                  selectedDocument={selectedDocument}
                  pages={pages}
                  setPages={setPages}
                  loading={loading}
                  selectedFiles={selectedFiles}
                  setSelectedPagesKeys={setSelectedPagesKeys}
                />
              </DndProvider>

              <SavedDocuments
                savedDocuments={savedDocuments}
                onEdit={onEdit}
                onRemove={onRemove}
                documents={documents}
                onPreview={onPreview}
              />

              {preview && (
                <PdfPageViewer
                  open={open}
                  setOpen={setOpen}
                  pages={pages}
                  setPages={setPages}
                  pageIndex={preview.page}
                  preview={preview}
                  setPreview={setPreview}
                />
              )}
            </>
          )}
          <Dialog
            open={deleteBufferFilesDialog}
            onClose={() => setDeleteBufferFilesDialog(false)}
            closeButtonProps={{ sx: { display: 'none' } }}
            aria-labelledby='delete-buffer-files-actions-dialog-title'
            fullWidth={true}
            maxWidth={'sm'}
            transitionDuration={0}
            scroll='body'
            title={t('BulkPdf.BufferTempFilesDialog.Title')}
            content={t('BulkPdf.BufferTempFilesDialog.Subtitle')}
            actions={
              <BoxActionsWrapper forDialog>
                <Button
                  onClick={() => {
                    onDeleteBufferTempFiles();
                    setDeleteBufferFilesDialog(false);
                  }}
                >
                  {t('Delete')}
                </Button>
                <Button onClick={() => setDeleteBufferFilesDialog(false)} variant='outlined'>
                  {t('Cancel')}
                </Button>
              </BoxActionsWrapper>
            }
          />
        </>
      }
      actions={
        <BoxActionsWrapper forDialog>
          <Button variant='outlined' onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button onClick={onSave} disabled={Object.keys(savedDocuments).length === 0}>
            {t('BulkPdf.Done')}
          </Button>
        </BoxActionsWrapper>
      }
    />
  );
};

BulkDocumentsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  documents: PropTypes.array.isRequired,
  show: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  saveDocument: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired
};

export default BulkDocumentsContainer;
